import type { BlockId, BlockType, ComponentsUnionType } from '@unifyapps/defs/types/block';
import type { SlotType } from '@unifyapps/defs/types/slot';
import _keyBy from 'lodash/keyBy';
import omit from 'lodash/omit';
import { DEPENDENCY_KEYS_TO_OMIT_DURING_SERIALIZATION } from '../no-code/dependency/const';
import { isEmptyBlock } from '../no-code/utils/block';
import type { BuildDependencyGraphItem } from '../dependency/utils/buildDependencyGraph';
import type { DynamicAndConditionalPathHandler } from '../no-code/context/DependencyGraphContext/handlers';
import { DependencyHelper } from '../dependency/helpers/DependencyHelper';
import { ContainerComponentType, StackComponentType } from '@unifyapps/defs/types/blocks';

// eslint-disable-next-line @typescript-eslint/no-extraneous-class -- false positive
export class BlockHelper {
  static getComponentBlockIds(block: BlockType) {
    // @ts-expect-error -- blockIds and config.blockIds are present
    if ('blockId' in block.component.content) {
      return [(block.component as { content: { blockId: BlockId } }).content.blockId];
    }

    // @ts-expect-error -- blockIds and config.blockIds are present
    return (block.component.blockIds ??
      // @ts-expect-error -- blockIds and config.blockIds are present
      (block.component.content as { blockIds: string[] } | undefined)?.blockIds) as
      | BlockId[]
      | undefined;
  }

  static getBlockSlots(block: BlockType) {
    return ('slots' in block.component ? block.component.slots : {}) as
      | Record<string, SlotType | undefined>
      | undefined;
  }

  static getBlockItems(block: BlockType) {
    return 'content' in block.component && 'items' in block.component.content
      ? block.component.content.items
      : [];
  }

  static shouldAddEmptyComponent(blockId?: BlockId) {
    // NOTE: if blockId is null, it means it's an empty block, and we should add an add component button
    return isEmptyBlock(blockId);
  }
  static getBlocksMapById(blocks: BlockType[]) {
    return _keyBy(blocks, 'id');
  }

  static getBlocksByComponentTypes(
    blocks: BlockType[],
    componentType: ComponentsUnionType['componentType'] | ComponentsUnionType['componentType'][],
  ) {
    const componentTypes = Array.isArray(componentType) ? componentType : [componentType];

    return blocks.filter((block) => componentTypes.includes(block.component.componentType));
  }

  static getDynamicPaths(block?: BlockType) {
    return block?.dP;
  }

  static getConditionalPaths(block?: BlockType) {
    return block?.cP;
  }

  static getDependsOn(block?: BlockType) {
    return block?.dpOn;
  }
  static isBlockStackOrContainer(
    block: BlockType,
  ): block is BlockType<StackComponentType | ContainerComponentType> {
    return (
      block.component.componentType === 'Stack' || block.component.componentType === 'Container'
    );
  }

  static extractBlockDependencyKeys(
    dep?: BuildDependencyGraphItem<DynamicAndConditionalPathHandler>,
  ): Pick<BlockType, 'cP' | 'dP' | 'dpOn'> {
    return {
      dpOn: DependencyHelper.getDependsOn(dep),
      cP: DependencyHelper.getConditionalPaths(dep),
      dP: DependencyHelper.getDynamicPaths(dep),
    };
  }

  static getDataSourceIds(block?: BlockType) {
    if (!block) {
      return [];
    }

    if (
      block.component.componentType === 'Filters' ||
      block.component.componentType === 'CategoryFilter'
    ) {
      // we are also spreading block.dataSourceIds
      // because some of the other data source can be referenced in the block via pill
      return Array.from(
        new Set([...(block.component.content.dataSourceIds ?? []), ...(block.dataSourceIds ?? [])]),
      );
    }

    return Array.from(new Set(block.dataSourceIds ?? []));
  }
  /**
   * Returns a copy of the block with events and dependency-related keys removed.
   * This is used when we need to reference data source IDs without including
   * computed dependency information or event handlers because events are fired on demand
   * and deps keys are not the actual source code.
   *
   * @param block - The block to process
   * @returns A filtered copy of the block without events and dependency keys
   */
  static getBlockForReferencingDataSourceIds(block: BlockType) {
    const { events: _, ...rest } = block;
    return omit(rest, DEPENDENCY_KEYS_TO_OMIT_DURING_SERIALIZATION);
  }

  static isLayoutBlock(block: BlockType) {
    return (
      block.component.componentType === 'Stack' || block.component.componentType === 'Container'
    );
  }
}
